import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import apiPaths from "../../../api/endpoints";
import { useRetrieveContent } from "../../hooks/useRetrieveContent";
import Lottie from "lottie-react";
import AudioRecorder, {
  AudioRecorderHandle,
} from "../KidsJournal/components/AudioRecorder/AudioRecorder";
import ConfirmationPopup from "../KidsJournal/components/ConfirmationPopup/ConfirmationPopup";
import SuccessPopup from "../Doodling/components/SuccessPopup/SuccessPopup";
import ChatBoxHeader from "./components/ChatBoxHeader/ChatBoxHeader";
import MiddleView from "./components/MiddleView/MiddleView";
import { chatBG, tellStoryBG } from "../../appAssets";
import studentLoginYetiJson from "../../appAssets/jsons/student_login_yeti.json";
import { CHATBOX_STRINGS, RetrieveContentItem } from "./types";
import "./ChatBoxScreen.scss";

import { trackEvent } from "../../../mixpanel";
import { TrackingEvents } from "../../Utilities/analytics";

/**
 * Mirrors srcMobile/Screens/FunBoxScreen/ChatBoxScreen/index.tsx
 *
 * Modes (driven by ?mode=chat | ?mode=story query param, default = chat):
 *   • chat  → fetches /retrieveContent/Chat, POSTs as SELActivity / ProfileQuestion
 *   • story → fetches /retrieveContent/Story, POSTs as Story
 *
 * Flow:
 *   1. Fetch questions (resumes at first un-answered)
 *   2. Show TypeWriter question + question audio
 *   3. AudioRecorder captures answer
 *   4. Save → POST FormData → SuccessPopup → close
 *   5. Closing with unsaved audio → ConfirmationPopup
 */
function ChatBoxScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Params can arrive either via router state (from WebStoryBreakScreen) or URL search params.
  // State takes precedence so nested-navigator flows work without query-string pollution.
  const routeState = (location.state ?? {}) as {
    navigatedFrom?: string;
    contentID?: string;
    isChatScreen?: boolean;
    isLastBlock?: boolean;
    isHeartModule?: boolean;
    storyId?: string;
    updatedMainStory?: () => void;
  };

  const isChatScreen =
    routeState.isChatScreen !== undefined
      ? routeState.isChatScreen
      : searchParams.get("mode") === "chat";
  const contentID = routeState.contentID ?? searchParams.get("contentID") ?? "";
  const navigatedFrom =
    routeState.navigatedFrom ?? searchParams.get("navigatedFrom") ?? "";
  const isLastBlock =
    routeState.isLastBlock ?? searchParams.get("isLastBlock") === "true";
  const isHeartModule =
    routeState.isHeartModule ?? searchParams.get("isHeartModule") === "true";
  const storyRefID = routeState.storyId ?? searchParams.get("storyId") ?? "";

  const updatedMainStoryVal =
    routeState.updatedMainStory ?? searchParams.get("updatedMainStory") ?? "";

  const { getRetrieveContent, postRetrieveContent } = useRetrieveContent();
  const audioRecorderRef = useRef<AudioRecorderHandle>(null);

  const [currentQuestions, setCurrentQuestions] = useState<
    RetrieveContentItem[] | null
  >(null);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [isOwn, setIsOwn] = useState(false);
  const [playingStatus, setPlayingStatus] = useState(true);
  const [disableControls, setDisableControls] = useState(false);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ── Fetch questions (mobile fetchContentData)
  const fetchContentData = useCallback(async () => {
    setIsLoading(true);
    const basePath = isChatScreen
      ? apiPaths.RETRIEVE_CONTENT_CHAT
      : apiPaths.RETRIEVE_CONTENT_STORY;
    const url = contentID ? `${basePath}/${contentID}` : basePath;
    const responseData = await getRetrieveContent(url);
    setIsLoading(false);

    const list: RetrieveContentItem[] = responseData?.data ?? [];
    if (!contentID) {
      const idx = list.findIndex((item) => item?.answer);
      setCurrentQuestionIdx(idx + 1);
    } else {
      setCurrentQuestionIdx(0);
    }
    setCurrentQuestions(list);
  }, [contentID, getRetrieveContent, isChatScreen]);

  useEffect(() => {
    fetchContentData();
  }, [fetchContentData]);

  // Re-trigger question audio after switching questions (mobile: 1s grace)
  useEffect(() => {
    setShowAudioPlayer(false);
    setPlayingStatus(true);
    const t = setTimeout(() => setShowAudioPlayer(true), 1000);
    return () => clearTimeout(t);
  }, [currentQuestionIdx, currentQuestions]);

  // ── Save response → POST FormData
  const saveResponse = async () => {
    if (!audioBlob) return;
    setIsLoading(true);
    trackEvent(
      isChatScreen
        ? TrackingEvents.chat_response_saved
        : TrackingEvents.story_saved,
    );
    const current = currentQuestions?.[currentQuestionIdx];
    const formdata = new FormData();
    formdata.append("file", audioBlob, "recording.webm");
    if (!isOwn && current?.documentId) {
      formdata.append("contentId", current.documentId);
    }
    formdata.append(
      "question",
      !isOwn ? (current?.question ?? "") : CHATBOX_STRINGS.myStory,
    );
    if (current?.complexity) {
      formdata.append("complexity", current.complexity);
    }
    if (isChatScreen) {
      formdata.append("contentType", "SELActivity");
      formdata.append("contentSubType", "ProfileQuestion");
    } else {
      formdata.append("contentType", "Story");
    }
    await postRetrieveContent(formdata);
    setIsLoading(false);
    setShowSuccess(true);
  };

  /**
   * Close — mirrors mobile onPress():
   *  1. Mid-recording (`disableControls`)            → confirmation popup
   *  2. ContentID + isLastBlock | isHeartModule      → reset to rooms
   *  3. Unsaved audio (`audioBlob`)                  → confirmation popup
   *  4. navigatedFrom story | storyBreakScreen        → go back (no popup)
   *  5. navigatedFrom heartcorner | hometemplate      → reset to rooms
   *  6. Default                                       → reset to rooms
   */
  const handleClose = () => {
    if (disableControls) {
      setShowConfirmation(true);
      return;
    }

    if (contentID && (isLastBlock || isHeartModule)) {
      audioRecorderRef.current?.stopPlayback();
      navigate("/web/rooms", { replace: true });
      return;
    }

    if (audioBlob) {
      setShowConfirmation(true);
      return;
    }

    audioRecorderRef.current?.stopPlayback();
    const from = navigatedFrom?.toLowerCase();

    if (from === "story" || from === "storybreakscreen") {
      navigate("/web/WebStoryScreen", {
        state: {
          storyId: storyRefID,
        },
      });
    } else {
      // heartcorner | hometemplate | default → mobile resets to HomeStack
      navigate("/web/rooms", { replace: true });
    }
  };

  // ── Shuffle: next question (mobile shuffleBtn)
  const handleShuffle = () => {
    if (disableControls) return;
    setPlayingStatus(true);
    setAudioBlob(null);
    setAudioUrl("");
    if (!currentQuestions) return;
    if (currentQuestionIdx >= currentQuestions.length - 1) {
      setCurrentQuestionIdx(0);
    } else {
      setCurrentQuestionIdx((i) => i + 1);
    }
  };

  const bgImage = isChatScreen ? chatBG : tellStoryBG;
  const questionAudio = currentQuestions?.[currentQuestionIdx]?.audio;

  return (
    <div className="chatbox" style={{ backgroundImage: `url(${bgImage})` }}>
      <ChatBoxHeader
        isChatScreen={isChatScreen}
        onClose={handleClose}
        hideClose={disableControls}
      />

      <MiddleView
        currentQuestions={currentQuestions}
        currentQuestionIdx={currentQuestionIdx}
        isOwnStory={isOwn}
        questionAudio={questionAudio}
        showAudioPlayer={showAudioPlayer && playingStatus}
      />

      <div className="chatbox__recorder">
        <AudioRecorder
          ref={audioRecorderRef}
          onRecorded={(url, blob) => {
            setAudioUrl(url);
            setAudioBlob(blob);
          }}
          onRecordingChange={(rec) => setDisableControls(rec)}
          onSave={saveResponse}
          showSaveBtn={true}
          showShuffleBtn={!isChatScreen && !isOwn}
          onShuffle={handleShuffle}
          isShowOwnBtn={true}
          isOwn={isOwn}
          toggleOwnStory={() => setIsOwn((v) => !v)}
          disableState={disableControls}
        />
      </div>

      {/* Bottom-right Yeti — local Lottie, partially off-screen (mobile: studentLoginYeti) */}
      <div className="chatbox__yeti">
        <Lottie
          animationData={studentLoginYetiJson}
          loop
          autoplay
          className="chatbox__yeti-lottie"
        />
      </div>

      {/* Question audio is now rendered inline inside MiddleView next to the text */}

      {/* Popups */}
      {showConfirmation && (
        <ConfirmationPopup
          onYes={() => {
            setShowConfirmation(false);
            saveResponse();
          }}
          onNo={() => {
            setShowConfirmation(false);
            setAudioBlob(null);
            setAudioUrl("");
            // Mobile handleCancel → reset to HomeStack
            audioRecorderRef.current?.stopPlayback();
            navigate("/web/rooms", { replace: true });
          }}
          onClose={() => setShowConfirmation(false)}
        />
      )}

      {showSuccess && (
        <SuccessPopup
          titleText={CHATBOX_STRINGS.thatsAmazing}
          btnText={CHATBOX_STRINGS.woohoo}
          onPress={() => {
            setShowSuccess(false);
            navigate("/web/WebStoryScreen", {
              state: {
                storyId: storyRefID,
              },
            });
          }}
          onClose={() => navigate(-1)}
        />
      )}

      {isLoading && (
        <div className="chatbox__loader-overlay">
          <div className="chatbox__loader" />
        </div>
      )}

      {/* Keep blob URL alive while mounted */}
      {audioUrl && <audio src={audioUrl} style={{ display: "none" }} />}
    </div>
  );
}

export default ChatBoxScreen;
